import { AxiosResponse } from 'axios';
import { api } from './api';

type ApiErrorBody = {
  message?: string;
};

const readOperationalApiError = async (error: unknown): Promise<string | null> => {
  const data = (error as { response?: { data?: ApiErrorBody | Blob | string } })?.response?.data;
  if (!data)
  {
    return null;
  }

  if (typeof data === 'string' && data.trim())
  {
    return data;
  }

  if (typeof Blob !== 'undefined' && data instanceof Blob)
  {
    try
    {
      const text = await data.text();
      const parsed = JSON.parse(text) as ApiErrorBody;
      if (parsed.message && parsed.message.trim())
      {
        return parsed.message;
      }
    }
    catch
    {
      return null;
    }

    return null;
  }

  if (typeof data === 'object' && 'message' in data && typeof data.message === 'string' && data.message.trim())
  {
    return data.message;
  }

  return null;
};

export interface OperationalIndicatorCatalogItem {
  id: string;
  code: string;
  name: string;
  description: string;
  unit: string;
  comparator?: string | null;
  targetValue?: number | null;
  secondaryLimitValue?: number | null;
  chartKind: string;
  isSystem: boolean;
  sortOrder: number;
}

export interface OperationalIndicatorDistributionBucket {
  slug: string;
  count: number;
}

export interface OperationalIndicatorMetric {
  code: string;
  name: string;
  description: string;
  unit: string;
  comparator?: string | null;
  targetValue?: number | null;
  secondaryLimitValue?: number | null;
  chartKind: string;
  isSystem: boolean;
  availability: string;
  value?: number | null;
  secondaryValue?: number | null;
  numerator?: number | null;
  denominator?: number | null;
  sampleCount?: number | null;
  meetsTarget?: boolean | null;
  distribution?: OperationalIndicatorDistributionBucket[] | null;
}

export interface OperationalIndicatorsDashboard {
  fromUtc: string;
  toUtcExclusive: string;
  cohortCount: number;
  autoResolveEnabled: boolean;
  autoResolveInactivityMinutes?: number | null;
  metrics: OperationalIndicatorMetric[];
}

export type OperationalExportFormat = 'csv' | 'xlsx';

class OperationalIndicatorsService {
  async listCatalog(): Promise<OperationalIndicatorCatalogItem[]> {
    const response = await api.get<OperationalIndicatorCatalogItem[]>(
      '/support/operational-indicators'
    );
    return response.data;
  }

  async getDashboard(params?: {
    fromUtc?: string;
    toUtc?: string;
  }): Promise<OperationalIndicatorsDashboard> {
    try
    {
      const response = await api.get<OperationalIndicatorsDashboard>(
        '/support/operational-indicators/dashboard',
        { params }
      );
      return response.data;
    }
    catch (error)
    {
      const message = await readOperationalApiError(error);
      throw new Error(message ?? 'Erro ao carregar indicadores operacionais');
    }
  }

  async export(params: {
    fromUtc: string;
    toUtc: string;
    assigneeId?: string;
    leadTemperature?: string;
    format: OperationalExportFormat;
  }): Promise<void> {
    let response: AxiosResponse<Blob>;
    try
    {
      response = await api.get('/support/operational-indicators/export', {
        params,
        responseType: 'blob',
      });
    }
    catch (error)
    {
      const message = await readOperationalApiError(error);
      throw new Error(message ?? 'Erro ao exportar dados operacionais');
    }

    const contentDisposition = response.headers['content-disposition'] as string | undefined;
    const fallbackName = `indicadores-operacionais.${params.format === 'xlsx' ? 'xlsx' : 'csv'}`;
    let fileName = fallbackName;
    if (contentDisposition)
    {
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
      if (match?.[1])
      {
        fileName = match[1].replace(/['"]/g, '');
      }
    }

    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream',
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }
}

export const operationalIndicatorsService = new OperationalIndicatorsService();
